import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Eye } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const EffectiveCostPerMilleCalculator = () => {
  const [totalRevenue, setTotalRevenue] = useState(5000);
  const [impressions, setImpressions] = useState(1000000);
  const [eCPM, setECPM] = useState(0);

  useEffect(() => {
    calculateECPM();
  }, [totalRevenue, impressions]);

  const calculateECPM = () => {
    const calculatedECPM = (totalRevenue / impressions) * 1000;
    setECPM(parseFloat(calculatedECPM.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/effective-cost-per-mille-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>eCPM Calculator: Measure Ad Revenue Efficiency | Free Tool</title>
        <meta name="description" content="Calculate your Effective Cost Per Mille (eCPM) instantly. Compare with industry benchmarks, get optimization tips, and improve ad monetization." />
        <meta name="keywords" content="eCPM calculator, effective CPM, ad revenue metrics, publisher monetization, ad yield optimization" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free eCPM Calculator: Optimize Ad Revenue" />
        <meta property="og:description" content="Calculate your Effective Cost Per Mille instantly. Get actionable insights to improve ad monetization and boost revenue." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free eCPM Calculator: Optimize Ad Revenue" />
        <meta property="twitter:description" content="Calculate your Effective Cost Per Mille instantly. Get actionable insights to improve ad monetization and boost revenue." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Effective Cost Per Mille Calculator",
            "description": "Free calculator to measure and optimize ad revenue per thousand impressions",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate eCPM",
              "Industry Benchmarks",
              "Optimization Tips",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Effective Cost Per Mille",
              "description": "The revenue generated per thousand ad impressions, measuring ad monetization effectiveness."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Publishers, Ad Operations Managers, Monetization Specialists"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate eCPM",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter total revenue"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input number of impressions"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your eCPM and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Effective Cost Per Mille (eCPM) Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Revenue</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalRevenue}
                  onChange={(e) => setTotalRevenue(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="5000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Impressions</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Eye className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={impressions}
                  onChange={(e) => setImpressions(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="1000000"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            ${eCPM}
            <span className="text-xl text-gray-500 ml-2">per 1000 impressions</span>
          </div>
          <p className="text-gray-600">Effective Cost Per Mille (eCPM)</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Effective Cost Per Mille (eCPM) is ${eCPM}. This means you're earning an average of ${eCPM} for every 1,000 ad impressions.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Format Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Display Ads: $1.00-3.00</li>
          <li>Video Ads: $12.00-20.00</li>
          <li>Native Ads: $5.00-10.00</li>
          <li>Mobile Ads: $1.00-4.00</li>
          <li>Rich Media: $4.00-8.00</li>
          <li>Interstitials: $6.00-12.00</li>
          <li>Rewarded Ads: $10.00-15.00</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Revenue Optimization Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Optimize ad placements</li>
          <li>Test ad formats</li>
          <li>Improve content quality</li>
          <li>Use header bidding</li>
          <li>Implement lazy loading</li>
          <li>Segment audiences</li>
          <li>Clean ad stack</li>
          <li>Mix ad types</li>
          <li>Monitor viewability</li>
          <li>Test pricing models</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Yield Factors</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Ad Viewability</li>
          <li>Fill Rate</li>
          <li>Page Load Time</li>
          <li>User Engagement</li>
          <li>Ad Density</li>
          <li>Device Type</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact Analysis</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Header bidding: +30% eCPM</li>
          <li>Viewability optimization: +25% eCPM</li>
          <li>Audience targeting: +40% eCPM</li>
          <li>Ad placement testing: +20% eCPM</li>
          <li>Format optimization: +35% eCPM</li>
        </ul>
      </div>
    </div>
  );
};

export default EffectiveCostPerMilleCalculator;
